import React from "react";

import DockedControls from "./DockedControls";
import CameraCard from "orion-components/Profiles/Widgets/Cameras/components/CameraCard";
import { Translate } from "orion-components/i18n";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { BorderlessCameraCard } from "../../../CBComponents";
import { checkUserIntPermissionByFeedId } from "orion-components/Session/Selectors";

const propTypes = {
	selectFloorPlanOn: PropTypes.func,
	floorPlansWithFacilityFeed: PropTypes.object
};

const defaultProps = {
	selectFloorPlanOn: () => {},
	floorPlansWithFacilityFeed: null
};

const CameraDockModule = ({
	cameraPosition,
	dockedCameras,
	camera,
	videoTour,
	tourCameras,
	userCameras,
	videoTours,
	addToDock,
	removeDockedCamera,
	cameraView,
	setCameraPriority,
	setFindNearestMode,
	findNearestMode,
	findNearestPosition,
	clearCameraReplaceMode,
	dialog,
	openDialog,
	closeDialog,
	loadProfile,
	fullscreenCameraOpen,
	sidebarOpen,
	user,
	readOnly,
	playbackStartTime,
	playBarValue,
	playbackPlaying,
	currentReplayMedia,
	addReplayMedia,
	removeReplayMedia,
	dir,
	selectFloorPlanOn,
	floorPlansWithFacilityFeed,
	cameraReplaceMode
}) => {
	const dispatch = useDispatch();

	const canControl = useSelector(
		state => !readOnly && checkUserIntPermissionByFeedId(state)(camera?.feedId, "control")
	);

	const handleCameraReplace = newCameraId => {
		dispatch(removeDockedCamera(cameraPosition, dockedCameras));
		setTimeout(() => {
			dispatch(addToDock(newCameraId, cameraPosition, dockedCameras));
			dispatch(clearCameraReplaceMode());
		}, 100);
	};

	// set overlay mode
	const renderCameraDisplay = (cameraReplaceMode, newCamera) => {
		// If name of camera includes word camera, don't add it to the dock placeholder
		// Set camera name if camera exists
		let cameraTextFix = "Camera";
		let cameraName;
		if (newCamera) {
			cameraName = newCamera.entityData.properties.name;

			if (newCamera.entityData.properties.name.toLowerCase().includes("camera")) {
				cameraTextFix = "";
			}
		}

		// if no camera and not trying to add one, show 'add camera' controls
		if (camera === null && !videoTour && !cameraReplaceMode.replaceMode) {
			return (
				<DockedControls
					userCameras={userCameras} // cameras user has access to
					videoTours={videoTours}
					dockedCameras={dockedCameras} // cameras that are docked
					cameraPosition={cameraPosition} // index of camera in array
					addToDock={addToDock} // method to dock camera
					//addCamera={addCamera}
					cameraView={cameraView}
					setFindNearestMode={setFindNearestMode}
					findNearestMode={findNearestMode}
					findNearestPosition={findNearestPosition}
					dir={dir}
				/>
			);
			// if no camera and trying to replace, show camera add placeholder
		} else if (camera === null && !videoTour && cameraReplaceMode.replaceMode) {
			return (
				<div
					className="add-to-dock-placeholder"
					onClick={() => {
						dispatch(addToDock(newCamera.id, cameraPosition, dockedCameras));
						dispatch(clearCameraReplaceMode());
					}} data-testid="orion-components-dock-cameras-components-camera-dock-module-div"
				>
					{cameraTextFix == "Camera" ? (
						<Translate
							value="global.dock.cameras.camDockModule.addStatic"
							primaryValue={newCamera.entityData.properties.name}
						/>
					) : (
						<Translate
							value="global.dock.cameras.camDockModule.addDynamic"
							primaryValue={newCamera.entityData.properties.name}
							secondaryValue={cameraTextFix}
						/>
					)}
				</div>
			);
			// otherwise, render a camera, or a camera with an overlay if trying to replace
		} else {
			if (videoTour) {
				return (
					<div style={{ position: "relative", height: "100%", width: "100%" }}>
						<BorderlessCameraCard
							camera={tourCameras}
							dockedItemId={videoTour.id}
							isDockedVideoTour={true}
							namePrefix={videoTour.name ?? null}
							dwellTime={videoTour.dwellTime ?? null}
							disableVideoModeToggle={true}
							playbackStartTime={playbackStartTime}
							playBarValue={playBarValue}
							playbackPlaying={playbackPlaying}
							currentReplayMedia={currentReplayMedia}
							addReplayMedia={addReplayMedia}
							removeReplayMedia={removeReplayMedia}
							readOnly={!!readOnly}
						/>
					</div>
				);
			}

			return (
				<div style={{ position: "relative" }}>
					{cameraReplaceMode.replaceMode && (
						<p
							className="overlay-text"
							onClick={() => handleCameraReplace(newCamera.id)} // handle replacing docked camera data-testid="orion-components-dock-cameras-components-camera-dock-module-p"
						>
							{" "}
							{cameraName ? (
								<Translate value="global.dock.cameras.camDockModule.replaceWith" count={cameraName} />
							) : (
								""
							)}{" "}
						</p>
					)}
					{camera.player?.newPlayer ? (
						<div style={{ height: "100%", width: "100%" }}>
							<BorderlessCameraCard
								camera={camera}
								playbackStartTime={playbackStartTime}
								playBarValue={playBarValue}
								playbackPlaying={playbackPlaying}
								currentReplayMedia={currentReplayMedia}
								addReplayMedia={addReplayMedia}
								removeReplayMedia={removeReplayMedia}
								readOnly={!!readOnly}
							/>
						</div>
					) : (
						<div className={cameraReplaceMode.replaceMode ? "camera-overlay" : ""}>
							<CameraCard
								cameraIndex={cameraPosition}
								canUnlink={false}
								useCameraGeometry={true}
								loadProfile={loadProfile}
								camera={camera}
								canExpand={false}
								handleCardExpand={() => {}}
								canTarget={true}
								hasMenu={true}
								expanded={true}
								disableSlew={true}
								dockedCameras={dockedCameras}
								removeDockedCamera={removeDockedCamera}
								isInDock={true}
								dialog={dialog}
								openDialog={openDialog}
								closeDialog={closeDialog}
								readOnly={!!readOnly}
								playbackStartTime={playbackStartTime}
								playBarValue={playBarValue}
								playbackPlaying={playbackPlaying}
								currentReplayMedia={currentReplayMedia}
								addReplayMedia={addReplayMedia}
								removeReplayMedia={removeReplayMedia}
								canControl={canControl}
								subscriberRef={"dock"}
								setCameraPriority={setCameraPriority}
								fullscreenCamera={fullscreenCameraOpen}
								dir={dir}
								selectFloorPlanOn={selectFloorPlanOn}
								floorPlansWithFacilityFeed={floorPlansWithFacilityFeed}
							/>
						</div>
					)}
				</div>
			);
		}
	};
	// .active = boolean, .cameraId = id
	// cameraReplaceMode, cameraName, camera from camera widget
	const cameraDisplay = renderCameraDisplay(cameraReplaceMode, cameraReplaceMode.camera);

	return (
		<div>
			{/* Conditionally render modules to ensure unmount when not in use */}
			{sidebarOpen ? cameraDisplay : <div />}
		</div>
	);
};

CameraDockModule.propTypes = propTypes;
CameraDockModule.defaultProps = defaultProps;

export default CameraDockModule;
